import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import Autocomplete from "./Autocomplete";
import { fetchReport, resolveRepId, searchRepDirectory } from "./api";
import { margin, shortName, transformRows } from "./utils";
import SummaryTab from "./SummaryTab";
import CustomerTab from "./CustomerTab";
import OpportunitiesTab from "./OpportunitiesTab";
import { selectUser } from "../../../features/userSlice";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState, LoadingState } from "../../../components/UI/States";

const TABS = [
  { key: "summary", label: "Summary By Product Class" },
  { key: "customer", label: "Sales By Customer" },
  { key: "opportunities", label: "Opportunities By Product Class" },
];

const todayISO = () => {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${mm}-${dd}`;
};

const DATE_INPUT =
  "app-input h-10 min-w-[150px] rounded-xl border px-3 text-sm";

const FIELD_LABEL = "app-muted text-xs font-semibold";

const MENU_ITEM =
  "block w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-[color:var(--app-hover)]";

// Truth Chart 2.0 — sales rep margin report card, ported from the
// standalone tool into Node. Admin and Executive Team only (see
// RouteHandler). Data flows
// through /api/truthchart (Node server → Big C API → Bistrack).
const TruthChart = () => {
  const user = useSelector(selectUser);
  const userId = user?.id;
  const [rep, setRep] = useState(null);
  const [fromDate, setFromDate] = useState(
    () => `${new Date().getFullYear()}-01-01`
  );
  const [toDate, setToDate] = useState(todayISO);
  const [rows, setRows] = useState([]);
  const [info, setInfo] = useState(null); // snapshot of the last run's params
  const [runId, setRunId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("summary");
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const { pg1List, custList, overallCo } = useMemo(
    () => transformRows(rows),
    [rows]
  );

  const run = async () => {
    if (!rep) {
      setError("Select a sales rep from the list first.");
      return;
    }
    if (!fromDate || !toDate) {
      setError("Enter both From Date and To Date.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      // The dropdown is directory-backed (no SalesRepID), so resolve the
      // proc's @Rep through the Truth Chart rep search before running.
      const repId = await resolveRepId(rep.name, userId);
      if (!repId) {
        throw new Error(
          `Couldn't find a sales rep named "${rep.name}" in Bistrack.`
        );
      }
      const reportRows = await fetchReport({
        repId,
        fromDate,
        toDate,
        userId,
      });
      setRows(reportRows);
      setInfo({ repName: rep.name, fromDate, toDate });
      setRunId((r) => r + 1);
    } catch (e) {
      setError(e.message);
      setRows([]);
      setInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const onDateKeyDown = (e) => {
    if (e.key === "Enter") run();
  };

  // Two-sheet export mirroring the original: a pivoted summary plus the raw
  // customer/PG2 detail.
  const exportExcel = () => {
    if (!custList.length || !info) return;

    const wb = XLSX.utils.book_new();

    const headerRow = ["Customer", "Type", "Total Sales", "Total Margin %"];
    pg1List.forEach((pg1) => {
      headerRow.push(
        shortName(pg1.name) + " Sales",
        shortName(pg1.name) + " Margin %"
      );
    });
    const summaryData = [headerRow];
    custList.forEach((cust) => {
      const custMargin = margin(cust.totalSales, cust.totalCost);
      const row = [
        cust.name,
        cust.type,
        cust.totalSales,
        custMargin != null ? +custMargin.toFixed(2) : "",
      ];
      pg1List.forEach((pg1) => {
        const p = cust.pg1s[pg1.id];
        if (p && p.sales > 0) {
          const m = margin(p.sales, p.cost);
          row.push(p.sales, m != null ? +m.toFixed(2) : "");
        } else {
          row.push(0, "");
        }
      });
      summaryData.push(row);
    });
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(summaryData),
      "Summary By Product Class"
    );

    const detailData = [
      [
        "Customer",
        "Type",
        "PG1",
        "PG2",
        "Net Sales",
        "Total Cost",
        "Rep Margin %",
        "Target Margin %",
        "Over/Under %",
        "Color",
      ],
    ];
    rows.forEach((r) => {
      const hasSales = (r["TotalSales"] || 0) > 0;
      detailData.push([
        r["Customer"],
        r["Name"],
        r["PG1"],
        r["PG2"],
        r["TotalSales"] || 0,
        r["TotalCost"] || 0,
        hasSales ? +(r["TotalMargin"] || 0).toFixed(2) : "",
        +(r["PG2 Company"] || 0).toFixed(2),
        hasSales
          ? +((r["TotalMargin"] || 0) - (r["PG2 Company"] || 0)).toFixed(2)
          : "",
        r["Color"] === 1
          ? "Below"
          : r["Color"] === 2
          ? "Within"
          : r["Color"] === 3
          ? "Above"
          : "",
      ]);
    });
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(detailData),
      "Sales By Customer"
    );

    XLSX.writeFile(
      wb,
      `TruthChart_${info.repName.replace(/\s+/g, "_")}_${info.fromDate}_${info.toDate}.xlsx`
    );
  };

  const canExport = custList.length > 0 && !!info;

  return (
    <div className="app-canvas min-h-[calc(100vh-140px)] w-full">
      <PageShell size="full">
        <PageHeader
          icon={AssessmentIcon}
          tone="amber"
          eyebrow="Admin"
          eyebrowTo="/admin"
          title="Truth Chart 2.0"
          description={
            <>
              Sales rep margin report card from Bistrack.
              {info && (
                <span className="app-chip ml-2 align-middle font-semibold tabular-nums text-[color:var(--app-text)]">
                  {info.repName} | {info.fromDate} → {info.toDate}
                </span>
              )}
            </>
          }
        />

        <div className="space-y-4">
          {/* Filter bar */}
          {filtersVisible && (
            <div className="app-card rounded-2xl border p-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className={FIELD_LABEL}>Sales Rep Name</label>
                  <Autocomplete
                    placeholder="Type rep name…"
                    fetcher={searchRepDirectory}
                    onSelect={setRep}
                    renderItem={(r) => (
                      <>
                        <strong className="font-semibold">{r.name}</strong>
                        <span className="app-muted block text-xs font-normal">
                          {r.title}
                          {r.branch ? ` · ${r.branch}` : ""}
                        </span>
                      </>
                    )}
                    inputClassName="app-input"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className={FIELD_LABEL}>From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    onKeyDown={onDateKeyDown}
                    aria-label="From Date"
                    className={DATE_INPUT}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className={FIELD_LABEL}>To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    onKeyDown={onDateKeyDown}
                    aria-label="To Date"
                    className={DATE_INPUT}
                  />
                </div>
                <button
                  type="button"
                  onClick={run}
                  disabled={loading}
                  className="app-btn app-btn-primary h-10 px-6"
                >
                  Run
                </button>
              </div>
            </div>
          )}

          <div className="app-card rounded-2xl border">
            {/* Tab bar */}
            <div className="app-divider flex items-center gap-2 border-b px-3 py-2.5">
              <div
                role="tablist"
                aria-label="Truth Chart reports"
                className="flex min-w-0 items-center gap-0.5 overflow-x-auto rounded-xl bg-[color:var(--app-hover)] p-[3px]"
              >
                {TABS.map((tab) => (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.key}
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`min-h-[34px] select-none whitespace-nowrap rounded-lg px-3 text-xs font-semibold transition-colors sm:text-sm ${
                      activeTab === tab.key
                        ? "bg-white text-[color:var(--app-text)] shadow-sm dark:bg-white/[0.14] dark:shadow-none"
                        : "app-muted hover:text-[color:var(--app-text)]"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div ref={menuRef} className="relative ml-auto shrink-0">
                <button
                  type="button"
                  onClick={() => setMenuOpen((o) => !o)}
                  title="Options"
                  aria-label="Options"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  className="app-icon-btn"
                >
                  <MoreVertIcon fontSize="small" />
                </button>
                {menuOpen && (
                  <div
                    role="menu"
                    className="app-glass absolute right-0 top-full z-50 mt-1.5 min-w-[200px] overflow-hidden rounded-xl border p-1"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setFiltersVisible((v) => !v);
                        setMenuOpen(false);
                      }}
                      className={MENU_ITEM}
                    >
                      {filtersVisible ? "Hide Filters" : "Show Filters"}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        if (!canExport) return;
                        exportExcel();
                        setMenuOpen(false);
                      }}
                      aria-disabled={!canExport}
                      className={`${MENU_ITEM} ${
                        canExport ? "" : "cursor-default opacity-50 hover:bg-transparent"
                      }`}
                    >
                      Export to Excel
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Tab content */}
            <div className="min-h-[400px] p-3 sm:p-4">
              {loading && <LoadingState label="Loading report…" />}
              {!loading && error && (
                <EmptyState
                  icon={ErrorOutlineIcon}
                  title="Couldn't run the report"
                  description={error}
                />
              )}
              {!loading && !error && !info && (
                <EmptyState
                  icon={AssessmentIcon}
                  title="No report yet"
                  description={
                    <>
                      Enter a sales rep name and date range, then click{" "}
                      <strong>Run</strong>.
                    </>
                  }
                />
              )}

              {/* Report tabs stay mounted so sort/accordion state survives tab
                  switches; runId remounts them (resetting that state) on each
                  new Run, matching the original tool. */}
              {!loading && !error && info && (
                <>
                  <div className={activeTab === "summary" ? "" : "hidden"}>
                    <SummaryTab
                      key={`s${runId}`}
                      pg1List={pg1List}
                      custList={custList}
                      overallCo={overallCo}
                      info={info}
                    />
                  </div>
                  <div className={activeTab === "customer" ? "" : "hidden"}>
                    <CustomerTab
                      key={`c${runId}`}
                      custList={custList}
                      overallCo={overallCo}
                      info={info}
                    />
                  </div>
                  <div className={activeTab === "opportunities" ? "" : "hidden"}>
                    <OpportunitiesTab key={`o${runId}`} rows={rows} info={info} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </PageShell>
    </div>
  );
};

export default TruthChart;
